import React, { useState, useRef } from "react";
import { useParams,useNavigate } from "react-router-dom";
import "./PlanDetails.scss";
import HeroImg from "../Components/Layouts/HeroImg";

const tabs = {
  dogs: [
    {
      label: "Medicine",
      icon: "💊",
      content: "Explore our wide range of vet-approved medicines for dogs.",
      details: "Medicines include antibiotics for infections, dewormers, flea and tick treatments, anti-inflammatory drugs, pain relievers, joint supplements, heartworm preventatives, and probiotics.",
      tips: "Consult your vet before starting any medication. Store medicines safely and follow dosage instructions carefully."
    },
    {
      label: "Vaccination",
      icon: "💉",
      content: "Keep your dog protected with timely vaccinations and expert guidance.",
      details: "Core vaccines include rabies, parvovirus, distemper, and adenovirus. Non-core vaccines include leptospirosis, bordetella, and Lyme disease, depending on your region and lifestyle.",
      tips: "Follow the puppy vaccination schedule starting at 6-8 weeks. Keep a vaccination record and consult your vet for annual boosters."
    },
    {
      label: "Grooming",
      icon: "🛁",
      content: "Pamper your dog with quality grooming services and essentials.",
      details: "Grooming includes brushing, bathing, ear cleaning, nail trimming, coat trimming, teeth brushing, and gland expression.",
      tips: "Brush regularly to prevent shedding and tangles. Use breed-specific brushes and shampoos. Check ears and nails weekly."
    },
    {
      label: "Diet & Nutrition",
      icon: "🍽️",
      content: "Ensure your dog is getting the right nutrients.",
      details: "A balanced diet includes high-quality proteins, healthy fats, digestible carbohydrates, fiber, and essential vitamins and minerals. Choose age-appropriate food (puppy, adult, senior).",
      tips: "Avoid human snacks—especially chocolate, onions, grapes, and xylitol. Fresh water should be available at all times."
    },
    {
      label: "Exercise & Activities",
      icon: "🐾",
      content: "Keep your dog physically and mentally active.",
      details: "Activities include daily walks, interactive toys, fetch, swimming, agility courses, hiking, and tug-of-war. Mental stimulation prevents boredom and destructive behavior.",
      tips: "Tailor activity level to your dog’s age, size, and breed. Monitor for fatigue or overexertion, especially in hot weather."
    },
    {
      label: "Training & Behavior",
      icon: "🎓",
      content: "Build a strong bond through training and positive behavior reinforcement.",
      details: "Training involves basic commands (sit, stay, come), potty training, leash walking, socialization with other pets and people, and behavioral corrections.",
      tips: "Use reward-based methods. Avoid punishment. Be consistent, and train in short, focused sessions to keep your dog engaged."
    },
    {
      label: "Emergency Care",
      icon: "🚨",
      content: "Be ready to act fast during emergencies.",
      details: "Emergency signs include vomiting, diarrhea, lethargy, limping, bloated abdomen, choking, seizures, or trauma. Know where your nearest 24/7 vet clinic is located.",
      tips: "Keep an emergency kit with bandages, saline, thermometer, gloves, and vet contacts. Learn pet CPR and basic first aid."
    },
    {
      label: "Accessories",
      icon: "🧸",
      content: "Choose the right gear and toys for your dog.",
      details: "Includes collars, leashes, harnesses, beds, chew toys, interactive puzzles, clothes, ID tags, travel carriers, and feeding bowls.",
      tips: "Choose safe, durable, and size-appropriate accessories. Wash bedding and toys regularly to prevent infections."
    },
    {
      label: "Health Checkups",
      icon: "🩺",
      content: "Schedule regular health checkups for your dog's wellbeing.",
      details: "Annual vet visits include physical exams, dental checks, blood tests, weight monitoring, parasite checks, and early disease detection.",
      tips: "Even healthy dogs need yearly checkups. Senior dogs or those with chronic conditions may need more frequent visits."
    },
    {
      label: "Pet Insurance",
      icon: "📄",
      content: "Secure your dog’s health and your peace of mind with insurance.",
      details: "Covers illnesses, injuries, surgeries, emergency care, and some wellness treatments. Plans vary in premiums, deductibles, and exclusions.",
      tips: "Compare policies carefully. Enroll your pet while they’re young and healthy to avoid pre-existing condition clauses."
    },
    {
      label: "Mental Wellbeing",
      icon: "🧠",
      content: "Support your dog’s emotional and psychological needs.",
      details: "Includes stress management, enrichment, companionship, consistency in routines, and handling separation anxiety. Mental health is just as important as physical health.",
      tips: "Offer puzzle toys, interactive play, safe spaces, and regular bonding time. Watch for signs of stress like pacing, whining, or aggression."
    },
    {
      label: "Travel & Relocation",
      icon: "✈️",
      content: "Prepare your dog for safe and stress-free travel.",
      details: "Cover essentials like car restraints, airline crate requirements, ID tags, travel water bowls, vaccinations, and health certifications.",
      tips: "Acclimate your dog to crates ahead of time. Bring familiar items and maintain feeding/walking schedules on the road."
    },
    {
      label: "Adoption & Rescue",
      icon: "🏠",
      content: "Welcome a rescued or adopted dog with care and patience.",
      details: "Learn about their history, provide structure, and establish trust. Rescue dogs may need time to adjust and unlearn past traumas.",
      tips: "Create a quiet, safe space. Use positive reinforcement. Don’t overwhelm them—let them adapt gradually."
    },
    {
      label: "Senior Pet Care",
      icon: "👵🐾",
      content: "Give your senior dog the gentle, attentive care they deserve.",
      details: "Includes age-appropriate diets, joint support, cognitive function supplements, and more frequent vet visits.",
      tips: "Provide orthopedic beds, avoid high jumps, and use ramps. Monitor for vision or hearing loss."
    },
    {
      label: "Breeding & Reproduction",
      icon: "🐣",
      content: "Understand the responsibilities of dog breeding.",
      details: "Know the reproductive cycle, signs of pregnancy, and proper care before, during, and after whelping.",
      tips: "Only breed healthy, vetted dogs. Be prepared for veterinary intervention and puppy care."
    },
    {
      label: "End-of-Life & Grief Support",
      icon: "🌈",
      content: "Handle your dog's final days with love and compassion.",
      details: "Learn about palliative care, euthanasia decisions, and ways to support yourself through grief.",
      tips: "Spend quality time together. Capture memories. Don’t hesitate to seek emotional support from friends or pet loss groups."
    }
  ],
  cats: [
    {
      label: "Medicine",
      icon: "💊",
      content: "Explore our wide range of vet-approved medicines for cats.",
      details: "Includes antibiotics for infections, flea control, dewormers, pain relievers, joint supplements, thyroid medications, and probiotics.",
      tips: "Never use dog or human meds for cats—always check with a vet before administering any medication."
    },
    {
      label: "Vaccination",
      icon: "💉",
      content: "Keep your cat protected with vaccines and guidance.",
      details: "Core vaccines include rabies, FVRCP (feline viral rhinotracheitis, calicivirus, panleukopenia). Non-core vaccines may include feline leukemia (FeLV) and feline immunodeficiency virus (FIV), depending on risk factors.",
      tips: "Yearly boosters are recommended even for indoor cats. Follow the vet's guidance for the appropriate vaccine schedule."
    },
    {
      label: "Grooming",
      icon: "🛁",
      content: "Pamper your cat with grooming essentials.",
      details: "Brushing, ear cleaning, nail trimming, occasional bathing (for long-haired breeds), and dental care are important parts of grooming.",
      tips: "Regular grooming prevents mats and hairballs. Use a gentle brush, especially for long-haired breeds."
    },
    {
      label: "Diet & Nutrition",
      icon: "🍽️",
      content: "Support your cat’s health with proper nutrition.",
      details: "A balanced diet includes high-quality protein, fats, and a small amount of carbohydrates. Dry food, wet food, and raw food options are available based on your cat's needs.",
      tips: "Fresh water is essential. Avoid giving your cat dog food, as it lacks the necessary nutrients. Monitor portion sizes to prevent obesity."
    },
    {
      label: "Exercise & Activities",
      icon: "🐾",
      content: "Stimulate your cat with physical and mental activities.",
      details: "Interactive toys like laser pointers, feather wands, puzzle feeders, climbing trees, and scratching posts help keep your cat engaged.",
      tips: "Engage your cat in daily play to prevent boredom and obesity. Rotate toys to keep things exciting."
    },
    {
      label: "Training & Behavior",
      icon: "🎓",
      content: "Understand your cat's behavior and encourage good habits.",
      details: "Training includes litter box training, teaching scratching alternatives, and encouraging socialization with other pets.",
      tips: "Never punish your cat—redirect undesirable behaviors with toys or treats. Be patient and positive in training."
    },
    {
      label: "Emergency Care",
      icon: "🚨",
      content: "Know how to handle urgent health issues.",
      details: "Signs of an emergency include vomiting, difficulty breathing, sudden aggression, hiding, and excessive drooling.",
      tips: "Always keep a carrier handy for trips to the vet. Learn basic first aid for cats and keep a list of emergency contacts."
    },
    {
      label: "Accessories",
      icon: "🧸",
      content: "Comfort your cat with the right accessories.",
      details: "Essential accessories include scratching posts, beds, collars with bells, food dishes, water fountains, and toys.",
      tips: "Make sure collars are safety-breakaway to prevent accidents. Change the layout of toys or scratching posts to keep your cat engaged."
    },
    {
      label: "Health Checkups",
      icon: "🩺",
      content: "Schedule regular health checkups for your cat.",
      details: "Annual checkups should include a thorough physical exam, dental checkup, vaccinations, and possibly blood tests to check for underlying conditions.",
      tips: "Even if your cat appears healthy, regular health checkups help detect issues early. Senior cats should have more frequent visits."
    },
    {
      label: "Pet Insurance",
      icon: "📄",
      content: "Get peace of mind with pet insurance for your cat.",
      details: "Pet insurance covers unexpected veterinary expenses, including surgeries, emergencies, and long-term treatments. Some policies also cover wellness visits.",
      tips: "Choose a policy with good coverage and check for exclusions. It's best to enroll early when your cat is healthy to avoid pre-existing condition clauses."
    },{
      label: "Mental Wellbeing",
      icon: "🧠",
      content: "Support your cat’s emotional and psychological needs.",
      details: "Enrichment includes climbing spaces, hiding spots, scent stimulation, and interactive play. Cats can suffer from stress and anxiety too.",
      tips: "Use pheromone diffusers, maintain routines, and ensure plenty of vertical space. Watch for overgrooming or hiding behaviors."
    },
    {
      label: "Travel & Relocation",
      icon: "✈️",
      content: "Traveling with your cat requires extra care and planning.",
      details: "Includes proper carriers, travel litter solutions, feeding adjustments, vet certificates, and comfort strategies.",
      tips: "Let your cat explore the carrier in advance. Use calming sprays and cover the carrier during travel for a sense of security."
    },
    {
      label: "Adoption & Rescue",
      icon: "🏠",
      content: "Help your rescued cat settle into their forever home.",
      details: "Rescued cats may have trust issues or special needs. Give them time, structure, and a safe space to explore at their own pace.",
      tips: "Start with one quiet room. Slowly introduce them to new spaces, people, and pets. Patience is key."
    },
    {
      label: "Senior Pet Care",
      icon: "👵🐾",
      content: "Caring for an older cat involves adjustments to routine and health monitoring.",
      details: "Includes easier litter box access, dental health, arthritis support, kidney and thyroid checks, and special diets.",
      tips: "Place beds in warm, quiet spots. Consider raised food bowls and softer litter. Watch for behavior or appetite changes."
    },
    {
      label: "Breeding & Reproduction",
      icon: "🐣",
      content: "Get informed before deciding to breed your cat.",
      details: "Learn about the heat cycle, proper nutrition for queens, safe birthing practices, and kitten care essentials.",
      tips: "Avoid backyard breeding. Always consult a vet and be prepared for emergencies or complications."
    },
    {
      label: "End-of-Life & Grief Support",
      icon: "🌈",
      content: "Handle the final stages of your cat's life with grace and compassion.",
      details: "Support includes pain management, comfort measures, and emotional preparation. Honor your bond through loving care.",
      tips: "Speak to your vet about quality of life assessments. Create a calm, cozy environment and be present during their final moments."
    }
  ],

  birds: [
    {
      label: "Medicine",
      icon: "💊",
      content: "Discover essential medications for your bird’s health.",
      details: "Medications include antibiotics, antiparasitics, antifungals, pain relievers, vitamin supplements, and probiotics. Avian-specific meds are important for safe treatment.",
      tips: "Consult an avian vet for accurate diagnosis and treatment plans. Never use human medications for birds."
    },
    {
      label: "Vaccination",
      icon: "💉",
      content: "Protect your bird with species-specific vaccines.",
      details: "Vaccines for birds may include polyomavirus, avian pox, Pacheco’s disease, and Newcastle disease, depending on the bird species and region.",
      tips: "Consult your vet for the necessary vaccines for your specific bird species. Keep a vaccination record for your bird."
    },
    {
      label: "Grooming",
      icon: "🛁",
      content: "Keep your bird clean and well-groomed.",
      details: "Regular grooming includes beak, nail, and feather care. You may also need to trim feathers or provide mist baths for some species.",
      tips: "Never cut blood feathers. Ensure perches are safe and comfortable, and use bird-safe grooming products."
    },
    {
      label: "Diet & Nutrition",
      icon: "🍽️",
      content: "Feed your bird a balanced and varied diet.",
      details: "Provide fresh fruits, vegetables, seeds, pellets, and grains that meet the dietary needs of your bird's species. Avoid avocado, chocolate, caffeine, and salty foods.",
      tips: "Fresh water should always be available. Introduce new foods gradually and remove any uneaten food to avoid spoilage."
    },
    {
      label: "Exercise & Activities",
      icon: "🐾",
      content: "Let your bird spread its wings—literally!",
      details: "Out-of-cage time, foraging activities, climbing trees, ladders, swings, and toys keep your bird mentally and physically engaged.",
      tips: "Supervise flight sessions and provide a safe space for free flight. Rotate toys to avoid boredom."
    },
    {
      label: "Training & Behavior",
      icon: "🎓",
      content: "Train your bird for better bonding and stimulation.",
      details: "Training methods include target training, step-up commands, and speech mimicry. Training can be a fun and rewarding bonding activity.",
      tips: "Use positive reinforcement such as treats and praise. Keep training sessions short and enjoyable."
    },
    {
      label: "Emergency Care",
      icon: "🚨",
      content: "Recognize avian emergencies early.",
      details: "Common signs include fluffed feathers, discharge from eyes or nose, difficulty breathing, lethargy, or unusual droppings.",
      tips: "Keep a travel cage ready for emergencies and have an emergency vet contact list handy. Immediate action is crucial for birds."
    },
    {
      label: "Accessories",
      icon: "🧸",
      content: "Equip your bird's cage with fun and comfort.",
      details: "Provide safe, bird-friendly perches, swings, mirrors, chew toys, food dispensers, and nesting material.",
      tips: "Avoid plastic or toxic materials in accessories. Rotate perches and toys frequently to provide stimulation."
    },
    {
      label: "Health Checkups",
      icon: "🩺",
      content: "Regular health checkups are essential for your bird's wellbeing.",
      details: "Annual checkups should include a physical exam, weight monitoring, beak and nail checks, and parasite screening.",
      tips: "Birds often hide illness, so early detection through regular exams is key. Schedule checkups with an avian vet."
    },
    {
      label: "Pet Insurance",
      icon: "📄",
      content: "Pet insurance can help cover the costs of unexpected health issues.",
      details: "Bird insurance can help with medical expenses for illnesses, injuries, and emergency vet visits. Plans vary in coverage and premiums.",
      tips: "Choose a plan that covers avian-specific care. Enroll early to avoid exclusions for pre-existing conditions."
    },{
      label: "Mental Wellbeing",
      icon: "🧠",
      content: "Keep your bird’s mind sharp and engaged.",
      details: "Mental stimulation includes foraging activities, varied perches, social interaction, and auditory/visual enrichment.",
      tips: "Rotate toys often. Talk to your bird, offer puzzle feeders, and avoid repetitive cage time to prevent stress."
    },
    {
      label: "Travel & Relocation",
      icon: "✈️",
      content: "Traveling with birds requires preparation and special care.",
      details: "Use airline-approved carriers, ensure temperature control, and bring food/water dishes. Travel can be stressful for birds.",
      tips: "Cover the carrier to reduce stress. Avoid travel during molting. Keep noise and motion to a minimum."
    },
    {
      label: "Adoption & Rescue",
      icon: "🏠",
      content: "Support your adopted bird’s transition into a new home.",
      details: "Birds may need extra attention, socialization, and time to adapt. Understand their previous environment if possible.",
      tips: "Go slow. Speak gently. Let them observe their new environment before expecting interaction."
    },
    {
      label: "Senior Pet Care",
      icon: "👵🐾",
      content: "Adjust care routines for aging birds.",
      details: "Older birds may have arthritis, vision problems, or dietary needs. Provide soft perches, easy-to-reach food, and warm environments.",
      tips: "Minimize cage climbing and stress. Schedule regular vet checkups and track changes in behavior or appetite."
    },
    {
      label: "Breeding & Reproduction",
      icon: "🐣",
      content: "Handle bird breeding with care and responsibility.",
      details: "Ensure pair compatibility, proper nesting space, calcium-rich diets, and safe incubation conditions.",
      tips: "Monitor egg laying for complications. Separate aggressive mates. Prepare for chick hand-rearing if necessary."
    },
    {
      label: "End-of-Life & Grief Support",
      icon: "🌈",
      content: "Care for your bird during their final stages of life.",
      details: "Birds often hide illness, so palliative care and attentive observation are vital. Say goodbye with kindness and dignity.",
      tips: "Create a calm environment. Speak softly and handle gently. Grief counseling is available if needed."
    }
  ]

};



const PlanDetails = () => {
  const { petType } = useParams(); 
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const contentRef = useRef(null);

  const petDetails = {
    dogs: {
      title: "Dog Care Guide",
      image: "/dog1.jpg",
      description:
        "A comprehensive guide to dog care, from health to grooming.",
    },
    cats: {
      title: "Cat Care Guide",
      image: "/cat1.jpg",
      description: "All you need to know to keep your cat healthy and happy.",
    },
    birds: {
      title: "Bird Care Guide",
      image: "/bird1.jpg",
      description: "Everything you need to care for your feathered friend.",
    },
  };

  const details = petDetails[petType] || {
    title: "Not Found",
    image: "/notfound.jpg",
    description: "Details unavailable.",
  };

  return (
    <>
    <HeroImg heading="Caring For Pets & Sheltering" text="Home / Care"/>
    <section className="plan-details--playful">
      <img
        src={details.image}
        alt={details.title}
        className="plan-details--playful__image"
      />
      <h2 className="plan-details--playful__title">{details.title}</h2>
      {/* <p>{details.description}</p> */}

      <div className="plan-details--playful__tabs">
        {tabs[petType].map((tab, index) => (
          <div
            key={index}
            className={`plan-details--playful__tab ${activeTab === index ? "active" : ""}`}
            onClick={() => {
              setActiveTab(index);
              contentRef.current?.scrollIntoView({ behavior: "smooth" });
              window.scrollTo(0, contentRef.current.offsetTop - 100); 
            }}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </div>
        ))}
      </div>

      <div className="plan-details--playful__content" ref={contentRef}>
      <h3>{tabs[petType][activeTab].label}</h3>
      <h4>{tabs[petType][activeTab].content}</h4>
      <p>Tips: {tabs[petType][activeTab].tips}</p>
      <p className="plan-details--playful__extra">{tabs[petType][activeTab].details}</p>
      </div>

      <button className="plan-details--playful__button" onClick={()=>navigate(-1)}>
        Back To Plan
      </button>
    </section>
    </>
  );
};

export default PlanDetails;
